/**
 * iter669 — Epson ePOS-Print Direct (Safari / Chrome on iPad).
 *
 * Sends receipts directly from the iPad browser to the TM-m30II's
 * built-in ePOS-Print HTTP service. Uses no PC Bridge, no iPrint app,
 * no AirPrint discovery. The same API TM Utility uses internally.
 *
 * ### Wire model
 *   Browser (Safari)
 *     ───POST XML───►  https://<printer-ip>/cgi-bin/epos/service.cgi
 *                       (mode: 'no-cors' — we can't read the response,
 *                        but the request fires and the printer prints)
 *
 * ### One-time setup per iPad
 *   The printer ships with a self-signed HTTPS cert. Safari blocks
 *   fetches to it until the user "vouches" for the cert by visiting
 *   `https://<printer-ip>/` once in Safari and tapping
 *   "Show Details → Visit this website" → "Visit Website".
 *   After that, Safari + every iOS browser remembers it for ~30 days.
 *
 * ### Why no-cors works for "fire and forget" print
 *   The TM-m30II doesn't send CORS headers, so a normal fetch would be
 *   blocked. `mode: 'no-cors'` lets the POST through; the response is
 *   opaque (status: 0, body unreadable) but the printer still receives
 *   and processes the request. Good enough — receipts don't need a
 *   confirmation body.
 *
 * Returns:
 *   { ok: true,  mode: 'epos-direct' }                 always (fire-and-forget)
 *   { ok: false, error: 'no-ip' | 'fetch-failed', msg } on hard failures
 */

/**
 * Build the Epson ePOS-Print SOAP-XML envelope from a flat receipt
 * payload. Mirrors the layout produced by _buildAirPrintHtml() in
 * printBridge.js so cashiers see the same paper.
 */
/**
 * iter681 — Render an arbitrary logo URL into an ePOS-Print
 * `<image>` element. Returns an empty string on any failure (network,
 * CORS, decoding) so the caller can safely concatenate it.
 *
 * Wire format (per Epson ePOS-Print XML spec):
 *   <image width="W" height="H" color="color_1" mode="mono">
 *     <base64 of raw 1-bit row-major pixel data, MSB first; 1 = black>
 *   </image>
 *
 * Steps:
 *   1. Load the image into an HTMLImageElement (CORS = anonymous so we
 *      can read its pixels through a canvas without taint).
 *   2. Scale to fit the printer width — usually 384px for 80mm rolls
 *      (50.8 dots/mm × ~75% safe area). We pick 384 for 80mm, 256 for
 *      58mm.
 *   3. Floyd–Steinberg dither into a single-channel monochrome buffer
 *      so photographic logos look right (not just hard-thresholded).
 *   4. Pack 8 pixels per byte (MSB first), base64 encode.
 */
async function _renderLogoEposImage(logoUrl, paperMm, logoScale = 1) {
  // iter727 — Returns { xml, status } so the caller can log what
  // happened (rendered / skipped / failed) into the Print Log. Before
  // this, a silent CORS failure looked identical to "no logo
  // configured" in the log — making the production "logo doesn't
  // print" bug invisible to operators.
  if (typeof window === 'undefined') return { xml: '', status: 'no-window' };
  if (typeof document === 'undefined') return { xml: '', status: 'no-document' };
  if (!logoUrl) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[ePOS] No thermal_logo_url configured for this tenant — skipping logo on receipt. Upload one under Settings → Branding → Thermal logo (black & white).');
    }
    return { xml: '', status: 'no-url' };
  }
  // iter727 — Load helper that tries WITH crossOrigin='anonymous'
  // first (required for true cross-origin canvas reads), and on
  // failure retries WITHOUT crossOrigin. The retry covers the case
  // where the backend serves the logo from the SAME origin but
  // doesn't include CORS headers in its response (FastAPI's
  // StaticFiles mount doesn't by default) — Safari/Chrome treat the
  // missing `Access-Control-Allow-Origin` as a CORS failure on the
  // image load even for same-origin URLs when `crossOrigin` is set.
  // The non-CORS load works for same-origin URLs because the canvas
  // is only tainted when the URL is truly cross-origin.
  async function loadImg(withCors) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      if (withCors) im.crossOrigin = 'anonymous';
      const timer = setTimeout(() => reject(new Error('logo load timeout (4s)')), 4000);
      im.onload = () => { clearTimeout(timer); resolve(im); };
      im.onerror = () => { clearTimeout(timer); reject(new Error(withCors ? 'logo load failed with CORS' : 'logo load failed without CORS')); };
      im.src = logoUrl;
    });
  }
  let attemptMode = 'cors';
  try {
    // iter767 — logoScale comes from branding.thermal_logo_size
    // (Small 0.75 / Medium 1.0 / Large 1.2). Clamp so we never exceed the
    // printable width or risk the m30II buffer.
    const _scale = Math.min(1.2, Math.max(0.6, Number(logoScale) || 1));
    const baseW = paperMm <= 58 ? 320 : 480;
    const targetW = Math.round(baseW * _scale);
    let img;
    try {
      img = await loadImg(true);
    } catch (corsErr) {
      // iter727 — Fall back to no-CORS for same-origin URLs that
      // happen to not advertise CORS headers (the common production
      // case where the user's logo lives under /api/uploads/...).
      attemptMode = 'no-cors';
      img = await loadImg(false);
    }

    // 2. Scale onto a canvas, preserving aspect, capped at targetW.
    //    iter736 — The hard height cap was 400 px which on a TM-m30II
    //    produces a 384×400 monochrome bitmap (~19 KB raw, ~28 KB after
    //    ePOS-Print hex encoding). Combined with a QR code XML the
    //    total payload reliably exceeds the per-job firmware buffer on
    //    the m30II / m30III family, which then ACKs the network packet
    //    as "OK" but silently drops the print — producing the exact
    //    "log says OK but no paper" symptom the cashier reports. iter767 —
    //    raised the cap from 160→240 dots (and width 384→480) because users
    //    reported the logo printing "very narrow". 480×240 stays well within
    //    the m30II raster buffer alongside a QR. If a low-buffer printer drops
    //    prints, dial these back.
    const MAX_LOGO_H_DOTS = Math.round(240 * _scale);
    const rawRatio = img.naturalHeight / Math.max(1, img.naturalWidth);
    let w = targetW;
    let h = Math.max(1, Math.round(targetW * rawRatio));
    if (h > MAX_LOGO_H_DOTS) {
      // Down-scale BOTH dimensions so the aspect stays intact instead
      // of squashing the image into the cap (which would look ugly on
      // tall logos like portrait wordmarks).
      const scale = MAX_LOGO_H_DOTS / h;
      w = Math.max(1, Math.round(targetW * scale));
      h = MAX_LOGO_H_DOTS;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    // iter727 — `getImageData` can throw SecurityError ("tainted
    // canvas") if we loaded without CORS AND the URL is truly cross-
    // origin. Catch that specifically so the caller knows WHY.
    let px;
    try {
      px = ctx.getImageData(0, 0, w, h).data;
    } catch (taintErr) {
      return { xml: '', status: `tainted-canvas (${attemptMode}, image is cross-origin without CORS headers)` };
    }

    // 3. Convert to grayscale Float32 buffer then Floyd–Steinberg dither.
    const gray = new Float32Array(w * h);
    for (let i = 0, j = 0; i < px.length; i += 4, j++) {
      gray[j] = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
    }
    const mono = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        const old = gray[idx];
        const newPx = old < 128 ? 0 : 255;
        mono[idx] = old < 128 ? 1 : 0;
        const err = old - newPx;
        if (x + 1 < w)               gray[idx + 1]     += err * 7 / 16;
        if (y + 1 < h && x > 0)      gray[idx + w - 1] += err * 3 / 16;
        if (y + 1 < h)               gray[idx + w]     += err * 5 / 16;
        if (y + 1 < h && x + 1 < w)  gray[idx + w + 1] += err * 1 / 16;
      }
    }

    // 4. Pack 8 pixels per byte, MSB first.
    const rowBytes = Math.ceil(w / 8);
    const packed = new Uint8Array(rowBytes * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (mono[y * w + x]) {
          packed[y * rowBytes + (x >> 3)] |= 0x80 >> (x & 7);
        }
      }
    }

    let bin = '';
    const CHUNK = 0x4000;
    for (let i = 0; i < packed.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, packed.subarray(i, Math.min(i + CHUNK, packed.length)));
    }
    const b64 = btoa(bin);

    return {
      // iter739 — Restore the official spec attributes `color="color_1"`
      // and `mode="mono"` per the Epson ePOS-Print XML reference. The
      // iter738 simplification was a guess that turned out to be the
      // wrong lead — the real root-cause was the `width="6"` → `size="6"`
      // fix on `<symbol>`. Keeping the canonical attributes here also
      // helps non-m30II Epson models that REQUIRE explicit color/mode.
      xml: `<image width="${w}" height="${h}" color="color_1" mode="mono">${b64}</image>`,
      status: `rendered-${attemptMode} (${w}x${h}, ${Math.round(b64.length / 1024)}KB)`,
    };
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[ePOS] Logo render skipped:', e && e.message);
    }
    return { xml: '', status: `failed (${(e && e.message) || 'unknown'})` };
  }
}

/**
 * iter683 — Build the structured line list that drives both the
 * ePOS XML emitter (for actual printing) AND the on-screen receipt
 * preview component. Returning a plain array of `{ text, bold, size }`
 * objects ensures the preview is *bit-for-bit identical* to what the
 * printer will produce (same padding, same wrapping, same divider
 * widths).
 *
 * @param {object} content — POS receipt payload (see buildPrintPayloadHelper).
 * @param {number} paperMm — Paper width in millimeters (58 or 80).
 * @returns {{ lines: Array<{text:string, bold?:boolean, size?:number}>, cols: number }}
 */
/**
 * iter753 — Columns-per-line for a given paper width, honouring the user's
 * Print Size setting. 'large' uses fewer columns → wider/larger characters
 * (and, in raster mode, a bigger font since fs = W / cols / 0.6).
 */
function _colsForPaper(paperMm, scale) {
  const sc = scale || getPrintScale();
  if (paperMm <= 58) return sc === 'large' ? 24 : 32;
  return sc === 'large' ? 32 : 42;
}

export function buildReceiptLines(content, paperMm) {
  let c = content || {};
  const opt = c.options || {};
  // iter727 — Both `show_order_box` and `show_zatca_qr` are now
  // opt-OUT (default TRUE). The iter686 opt-in safety was because
  // some older TM-m30II firmware silently rejected the XML envelope,
  // but the iter723 Content-Type fix has stabilised those firmwares,
  // and the printer is now confirmed working in production. Users
  // who want them off can still toggle them in Settings → Receipt
  // Options. (Empty `optOutKeys` set kept for future flags.)
  const on = (k, fallback) => {
    if (k in opt) return !!opt[k];
    if (fallback !== undefined) return fallback;
    const optOutKeys = new Set();
    return !optOutKeys.has(k);
  };

  // iter730 — Bilingual label translator. The Receipt Settings page
  // surfaces 3 controls (print_language, main_language, localized_
  // language) — they were never wired through to actual printing
  // until now. When mode='both' we emit "English / العربية" side-
  // by-side; when 'localized' we emit just the localized label;
  // when 'main' (or unset) we keep the existing English behaviour
  // so nothing changes for tenants that haven't touched the toggle.
  // Item names themselves are untranslated per the user's spec —
  // they print exactly as the cashier typed them into the menu.
  const LABELS = {
    en: { date: 'Date', printed_at: 'Printed At', check: 'Check#', order: 'Order',
          table: 'Table', by: 'By', qty: 'Qty', item: 'Item', price: 'Price',
          subtotal: 'Subtotal (Excl. VAT)', discount: 'Discount', vat: 'VAT Amount', total: 'Grand Total',
          payment: 'Payment', products_count: 'Products Count', vat_id: 'VAT',
          branch: 'Branch', invoice_no: 'Invoice No', invoice_date: 'Invoice Date', buyer: 'Buyer', buyer_vat: 'Buyer VAT', dine_in: 'Dine In', takeaway: 'Take Away', pickup: 'Pick Up', delivery: 'Delivery' },
    ar: { date: 'التاريخ', printed_at: 'تاريخ الطباعة', check: 'رقم الفاتورة',
          order: 'طلب', table: 'طاولة', by: 'بواسطة', qty: 'الكمية',
          item: 'الصنف', price: 'السعر', subtotal: 'المجموع (بدون الضريبة)', discount: 'الخصم',
          vat: 'مبلغ الضريبة', total: 'الإجمالي الكلي', payment: 'الدفع',
          products_count: 'عدد المنتجات', vat_id: 'الرقم الضريبي',
          branch: 'الفرع', invoice_no: 'رقم الفاتورة', invoice_date: 'تاريخ الفاتورة', buyer: 'المشتري', buyer_vat: 'الرقم الضريبي للمشتري', dine_in: 'محلي', takeaway: 'سفري', pickup: 'استلام', delivery: 'توصيل' },
    fr: { date: 'Date', printed_at: 'Imprimé le', check: 'N° chèque', order: 'Cmd',
          table: 'Table', by: 'Par', qty: 'Qté', item: 'Article', price: 'Prix',
          subtotal: 'Sous-total', discount: 'Remise', vat: 'TVA', total: 'Total',
          payment: 'Paiement', products_count: 'Nb articles', vat_id: 'N° TVA' },
    es: { date: 'Fecha', printed_at: 'Impreso el', check: 'N° cuenta', order: 'Pedido',
          table: 'Mesa', by: 'Por', qty: 'Cant.', item: 'Artículo', price: 'Precio',
          subtotal: 'Subtotal', discount: 'Descuento', vat: 'IVA', total: 'Total',
          payment: 'Pago', products_count: 'N° artículos', vat_id: 'NIF' },
  };
  // iter731 — On 58mm paper (32 cols) the bilingual side-by-side
  // column-header row would overflow ("Qty / الكمية  Item / الصنف ...
  // Price / السعر" is ~38 chars). For the few keys most likely to
  // collide with the right column, use abbreviated forms. Long
  // labels for less-frequent keys are unchanged. Only kicks in for
  // print_language='both' on paperMm<=58 so 80mm receipts keep
  // their full bilingual labels.
  const SHORT_LABELS = {
    en: { qty: 'Qty', item: 'Item', price: 'Pr', subtotal: 'Sub',
          discount: 'Disc', vat: 'VAT', total: 'Tot', payment: 'Pay',
          products_count: 'Items' },
    ar: { qty: 'كمية', item: 'صنف', price: 'سعر', subtotal: 'جزئي',
          discount: 'خصم', vat: 'ضريبة القيمة المضافة', total: 'إجمالي', payment: 'دفع',
          products_count: 'عدد' },
    fr: { qty: 'Qté', item: 'Art', price: 'Pr', subtotal: 'ST',
          discount: 'Rem', vat: 'TVA', total: 'Tot', payment: 'P',
          products_count: 'Art' },
    es: { qty: 'C', item: 'Art', price: 'Pr', subtotal: 'Sub',
          discount: 'Desc', vat: 'IVA', total: 'Tot', payment: 'P',
          products_count: 'Art' },
  };
  const printLang = c.print_language || 'main';
  const mainLang = LABELS[c.main_language] ? c.main_language : 'en';
  const locLang  = LABELS[c.localized_language] ? c.localized_language : 'ar';
  const compact = printLang === 'both' && paperMm <= 58;
  const pick = (lang, key) => {
    if (compact && SHORT_LABELS[lang]?.[key]) return SHORT_LABELS[lang][key];
    return LABELS[lang]?.[key] || LABELS.en[key] || key;
  };
  const T = (key) => {
    const m = pick(mainLang, key);
    const l = pick(locLang, key);
    if (printLang === 'localized') return l;
    if (printLang === 'both' && l && l !== m) {
      // iter731 — Tight `m/l` on 58mm compact mode (every column
      // counts), spaced `m / l` on 80mm where readability wins.
      return compact ? `${m}/${l}` : `${m} / ${l}`;
    }
    return m;
  };

  const cols = _colsForPaper(paperMm);
  const colsFor = (size) => (size === 2 ? Math.floor(cols / 2) : cols);

  const pad = (left, right, size = 1) => {
    const width = colsFor(size);
    const l = String(left || '');
    const r = String(right || '');
    const space = Math.max(1, width - l.length - r.length);
    return l + ' '.repeat(space) + r;
  };
  const padRow = (left, right, size = 1) => ({ text: pad(left, right, size), left: String(left || ''), right: String(right || '') });
  const center = (s, size = 1) => {
    const width = colsFor(size);
    const t = String(s || '');
    const padLeft = Math.max(0, Math.floor((width - t.length) / 2));
    return ' '.repeat(padLeft) + t;
  };
  const wrap = (s, size = 1) => {
    const width = colsFor(size);
    const words = String(s || '').split(/\s+/).filter(Boolean);
    const out = [];
    let cur = '';
    for (const w of words) {
      if ((cur + ' ' + w).trim().length <= width) {
        cur = (cur ? cur + ' ' : '') + w;
      } else {
        if (cur) out.push(cur);
        cur = w;
      }
    }
    if (cur) out.push(cur);
    return out.length ? out : [''];
  };
  const compactDate = (iso) => {
    const d = iso ? new Date(iso) : null;
    if (!d || isNaN(d.getTime())) return String(iso || '');
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  };
  // iter684 — Long-form date "2026/06/14 08:25:54 PM" matches the
  // Foodics-style sample receipt the user wants to clone.
  const longDate = (iso) => {
    const d = iso ? new Date(iso) : null;
    if (!d || isNaN(d.getTime())) return String(iso || '');
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    let hh = d.getHours();
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12 || 12;
    return `${yyyy}/${mm}/${dd} ${String(hh).padStart(2, '0')}:${mi}:${ss} ${ampm}`;
  };
  // iter686 — ASCII divider (was Unicode '─'). Same code-page safety
  // rationale as the Order# box above.
  const divider = '-'.repeat(cols);

  const lines = [];

  // Identity header — every name printed once: restaurant/brand (branch receipt header) →
  // branch → legal company name (EN/AR) → VAT → short address. Company names repeated in
  // the header text are de-duplicated (e.g. "SMART STANDARD CO" == "Smart Standard Company").
  const isKitchen = /^KITCHEN|^BAR/i.test(String(c.title || ''));
  // Saudi VAT-registered seller → every customer receipt is a ZATCA tax invoice:
  // Standard "Tax Invoice" when the buyer has a 15-digit VAT no. (same rule as the
  // ZATCA XML builder), otherwise "Simplified Tax Invoice".
  const saTax = !isKitchen && isSaudiTaxReceipt(c);
  const b2b = saTax && /^\d{15}$/.test(String(c.buyer_vat || '').trim());
  const _norm = (s) => String(s || '').toLowerCase()
    .replace(/\b(co|company|ltd|est|llc)\b\.?/g, '').replace(/[^\p{L}\p{N}]/gu, '');
  const _seen = [];
  const once = (txt, style = {}) => {
    const t = String(txt || '').trim();
    const k = _norm(t);
    if (!t || !k || _seen.some((x) => x === k || x.includes(k) || k.includes(x))) return;
    _seen.push(k);
    for (const ln of wrap(t, style.size)) lines.push({ align: 'center', text: center(ln, style.size), ...style });
  };
  const headerText = c.header || c.tenant_header_text || '';
  if (on('show_arabic_header') && headerText) {
    for (const ln of String(headerText).split('\n')) once(ln, { bold: true, size: 2 });
  }
  if (!isKitchen) {
    if (c.branch) once(`${T('branch')}: ${c.branch}`);
    once(c.company, { bold: true });
    once(c.zatca_seller_name, { bold: true });
  }
  if (lines.length) lines.push({ text: '' });

  // iter684 — Big boxed "Order# 77" centered, matching the sample.
  // iter686 — Switched from Unicode box-drawing (┌─┐│└┘) to plain
  // ASCII (+, -, |). Many TM-m30II printers boot with PC437 / PC850
  // code page where the Unicode chars get rendered as garbage or
  // dropped silently — causing the whole receipt to look broken on
  // some hardware. ASCII works on any code page.
  if (on('show_order_box', false) && c.order_number) {
    const inner = ` Order# ${c.order_number} `;
    const boxWidth = Math.min(colsFor(2), Math.max(inner.length + 2, 12));
    const horiz = '-'.repeat(boxWidth);
    lines.push({ align: 'center', text: center('+' + horiz + '+', 2), bold: true, size: 2 });
    const innerPadded = inner.padStart(Math.floor((boxWidth + inner.length) / 2)).padEnd(boxWidth);
    lines.push({ align: 'center', text: center('|' + innerPadded + '|', 2), bold: true, size: 2 });
    lines.push({ align: 'center', text: center('+' + horiz + '+', 2), bold: true, size: 2 });
    lines.push({ text: '' });
  }

  // VAT + short address, then the title (RECEIPT / KITCHEN ORDER / INVOICE) —
  // title only when no big order box is shown (otherwise redundant).
  if ((saTax || on('show_vat')) && c.vat) lines.push({ align: 'center', text: center(`${T('vat_id')}: ${c.vat}`) });
  if (!isKitchen && c.address) for (const ln of wrap(c.address)) lines.push({ align: 'center', text: center(ln) });
  if (saTax) {
    lines.push({ align: 'center', text: center(b2b ? 'فاتورة ضريبية' : 'فاتورة ضريبية مبسطة'), bold: true });
    lines.push({ align: 'center', text: center(b2b ? 'Tax Invoice' : 'Simplified Tax Invoice'), bold: true });
    if (b2b) {
      if (c.buyer_name) lines.push({ text: `${T('buyer')}: ${c.buyer_name}` });
      lines.push({ text: `${T('buyer_vat')}: ${String(c.buyer_vat).trim()}` });
    }
    // Required fields — always printed on a tax invoice, whatever the toggles.
    if (c.order_number) lines.push({ text: `${T('invoice_no')}: ${c.order_number}` });
    if (c.date) lines.push({ text: `${T('invoice_date')}: ${longDate(c.date)}` });
  } else if (!on('show_order_box') && c.title) {
    lines.push({ align: 'center', text: center(c.title), bold: true });
  }

  // Printed At + Check# meta.
  if (on('show_printed_at') && !saTax) {
    if (c.date) lines.push({ text: `${T('printed_at')}: ${longDate(c.date)}` });
  }
  const _ot = String(c.order_type || '').toLowerCase().replace(/[\s-]+/g, '_');
  const _otKey = { dine_in: 'dine_in', dinein: 'dine_in', takeaway: 'takeaway', take_away: 'takeaway',
    pick_up: 'pickup', pickup: 'pickup', delivery: 'delivery' }[_ot];
  if (_otKey) c = { ...c, order_type: T(_otKey) };
  if (on('show_order_type') && c.order_type) {
    // Sample: left-aligned label + right-aligned check number on same line.
    if (on('show_check_number') && c.check_number) {
      lines.push({ ...padRow(c.order_type, `${T('check')} ${c.check_number}`) });
    } else {
      lines.push({ text: c.order_type });
    }
  } else if (on('show_check_number') && c.check_number) {
    lines.push({ ...padRow('', `${T('check')} ${c.check_number}`) });
  }
  if (on('show_printed_at') && c.opened_at) {
    lines.push({ ...padRow('', longDate(c.opened_at)) });
  }

  lines.push({ text: divider });

  // Compact "Order: 77" line (kept as fallback when the box is OFF).
  if (!saTax && !on('show_order_box') && on('show_order_number') && c.order_number) {
    lines.push({ text: `${T('order')}: ${c.order_number}` });
  }
  if (!saTax && !on('show_printed_at') && c.date) {
    lines.push({ text: `${T('date')}:  ${compactDate(c.date)}` });
  }
  if (c.table) lines.push({ text: `${T('table')}: ${c.table}` });
  const who = c.server || c.cashier;
  if (who && on('show_creator_username')) lines.push({ text: `${T('by')}:    ${who}` });

  // Column headers row (Qty | Item | Price).
  if (on('show_column_headers') && (c.items || []).length > 0) {
    // Layout: "Qty  Item" left, "Price" right. Numbers are padded so
    // qty column is 4 chars wide which lines up nicely with the items.
    const header = `${T('qty')}  ${T('item')}`;
    const price = T('price');
    lines.push({ ...padRow(header, price), bold: true });
    lines.push({ text: divider });
  }

  // Items block — iter684 — Qty column-aligned to 4 chars, name to the
  // remainder, price right-aligned. Matches the user's sample receipt.
  for (const item of (c.items || [])) {
    const qty = item.qty || item.quantity || 1;
    const subRaw = item.subtotal ?? item.price ?? 0;
    const sub = Number(subRaw).toFixed(2);
    // iter684 — Skip free modifier rows if the operator has opted to
    // hide them. The parent line is still rendered.
    const isFree = Number(subRaw) === 0 && (item.modifier_of || item.is_modifier);
    if (isFree && on('hide_free_modifiers', true)) {
      // still render the parent line below; just skip this freebie.
      continue;
    }
    const qtyStr = String(qty).padEnd(4);
    const nameMax = colsFor(1) - 4 - sub.length - 1; // 4 qty + space + price width
    const itemName = String(item.name || '').slice(0, Math.max(8, nameMax));
    lines.push({ ...padRow(`${qtyStr}${itemName}`, `${c.currency || ''}${sub}`) });
    if (item.modifiers && item.modifiers.length) {
      for (const mod of item.modifiers) {
        // Render under qty column (indented 4 spaces).
        lines.push({ text: `    ${mod.name || mod}` });
      }
    }
    if (item.note) lines.push({ text: `    ${item.note}` });
  }
  lines.push({ text: divider });

  // Totals block.
  if ((saTax || on('show_subtotal')) && c.subtotal != null) {
    lines.push({ ...padRow(T('subtotal'), `${c.currency || ''}${Number(c.subtotal).toFixed(2)}`) });
  }
  if (c.discount && Number(c.discount) > 0) {
    lines.push({ ...padRow(T('discount'), `-${c.currency || ''}${Number(c.discount).toFixed(2)}`) });
  }
  if ((saTax || on('show_vat')) && c.tax != null) {
    const pct = c.tax_pct || 15;
    lines.push({ ...padRow(`${T('vat')} (${Number(pct).toFixed(0)}%)`, `${c.currency || ''}${Number(c.tax).toFixed(2)}`) });
  }
  if (c.total != null) {
    lines.push({ ...padRow(T('total'), `${c.currency || ''}${Number(c.total).toFixed(2)}`, 2), bold: true, size: 2 });
  }
  if (on('show_payment_method') && c.payment_method) {
    // Sample: "Payment - Bilad" left, total right.
    const label = c.payment_label ? `${T('payment')} - ${c.payment_label}` : `${T('payment')} - ${String(c.payment_method).toUpperCase()}`;
    lines.push({ ...padRow(label, `${c.currency || ''}${Number(c.total || 0).toFixed(2)}`) });
  }
  if (on('show_products_count') && (c.items || []).length > 0) {
    lines.push({ align: 'center', text: center(`${T('products_count')} ${(c.items || []).length}`) });
  }
  lines.push({ text: divider });

  // iter917 — Tailor MEASUREMENTS / MODIFICATION section (bilingual EN/AR),
  // printed above the footer when the sale carries tailor metadata.
  const tailor = tailorReceiptLines(c.metadata);
  if (tailor) {
    lines.push({ align: 'center', text: center(tailor.title), bold: true });
    for (const r of tailor.rows) lines.push({ ...padRow(r.label, r.value) });
    lines.push({ text: divider });
  }

  // Footer block (wrapped + centered).
  if (c.footer) {
    for (const ln of wrap(c.footer)) lines.push({ align: 'center', text: center(ln) });
  }

  // iter684 — Company name at very bottom (toggleable).
  if (on('show_company_at_bottom') && c.company) {
    lines.push({ text: '' });
    lines.push({ align: 'center', text: center(c.company) });
  }

  return { lines, cols };
}

/**
 * iter684 — Build a ZATCA-compliant TLV QR payload (Saudi Arabia's
 * Phase-1 e-invoicing requirement). Each tag is `[tagId 1B][len 1B][utf8 bytes]`,
 * concatenated, then base64-encoded.
 *
 *   Tag 1 — Seller Name (UTF-8)
 *   Tag 2 — VAT Registration Number
 *   Tag 3 — Invoice timestamp (ISO 8601 with timezone)
 *   Tag 4 — Invoice total (with VAT)
 *   Tag 5 — VAT amount
 *
 * Returns '' when required fields are missing — caller should then skip
 * rendering the QR rather than emit a malformed one (which would fail
 * a ZATCA validator scan).
 *
 * @param {object} c — receipt content payload.
 * @returns {string} base64-encoded TLV string or '' if invalid.
 */
// Saudi ZATCA invoice wording only for Saudi companies/branches (country setting), never globally.
export function isSaudiTaxReceipt(c) {
  const cc = String(c?.country_code || '').toUpperCase();
  const vat = String(c?.vat || '').trim();
  if (!vat || /^KITCHEN|^BAR/i.test(String(c?.title || ''))) return false;
  return cc ? cc === 'SA' : /^3\d{13}3$/.test(vat);
}

export function buildZatcaQrPayload(c) {
  // Phase 2: the signed invoice's own QR (from the ZATCA record) wins over the local TLV.
  if (c?.zatca_qr) return String(c.zatca_qr);
  const seller = (c?.zatca_seller_name || c?.company || '').trim();
  const vatNo = (c?.vat || '').trim();
  const iso = c?.date ? new Date(c.date).toISOString() : '';
  const total = (c?.total != null) ? Number(c.total).toFixed(2) : '';
  const vatAmt = (c?.tax != null) ? Number(c.tax).toFixed(2) : '';
  if (!seller || !vatNo || !iso || !total || !vatAmt) return '';
  const tlv = (tag, valueStr) => {
    const bytes = new TextEncoder().encode(String(valueStr));
    const out = new Uint8Array(2 + bytes.length);
    out[0] = tag;
    out[1] = bytes.length;
    out.set(bytes, 2);
    return out;
  };
  const chunks = [
    tlv(1, seller),
    tlv(2, vatNo),
    tlv(3, iso),
    tlv(4, total),
    tlv(5, vatAmt),
  ];
  const total_len = chunks.reduce((s, c2) => s + c2.length, 0);
  const buf = new Uint8Array(total_len);
  let off = 0;
  for (const ch of chunks) { buf.set(ch, off); off += ch.length; }
  // base64 encode (chunked for safety).
  let bin = '';
  const CHUNK = 0x4000;
  for (let i = 0; i < buf.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, buf.subarray(i, Math.min(i + CHUNK, buf.length)));
  }
  if (typeof btoa === 'undefined') {
    // Node fallback (used in smoke tests). Browser always has btoa.
    return Buffer.from(buf).toString('base64');
  }
  return btoa(bin);
}

/**
 * iter745 — Render the ENTIRE receipt (logo + every text line) into a
 * single monochrome bitmap and return it as one ePOS `<image>` element.
 *
 * Why: the TM-m30II's built-in ePOS-Print fonts do NOT include Arabic,
 * so bilingual receipts sent as `<text>` either print garbled or are
 * silently dropped. Rendering on a browser canvas lets the OS shape
 * Arabic (RTL + ligatures) correctly; the printer just prints dots.
 * This is the same approach Foodics and most KSA POS systems use.
 *
 * @param {object} content       Receipt payload.
 * @param {number} paperMm       58 or 80.
 * @param {number} resolutionScale (iter915) Optional (default 1). Values
 *   < 1 downscale the canvas width + font size proportionally so the
 *   final ePOS XML shrinks below the printer buffer limit WITHOUT
 *   losing Arabic shaping (used by the Arabic-safe oversized retry).
 *
 * Returns { xml, status }. On any failure returns xml:'' + a reason so
 * the caller can fall back to the text path and log why.
 */
async function _renderReceiptRasterImage(content, paperMm, resolutionScale = 1) {
  if (typeof document === 'undefined') return { xml: '', status: 'no-document' };
  const c = content || {};
  // iter753 — Print-too-small FIX. The raster <image> prints at its NATIVE
  // dot resolution, so the canvas width MUST match the printer's printable
  // dot width or the receipt shrinks into a narrow column. TM-m30II @203dpi:
  // 80mm paper → 72mm print → 576 dots; 58mm paper → 48mm print → 384 dots.
  // (Was 384/256 → printed at ~67% width, hence the "too small" reports.)
  const baseW = paperMm <= 58 ? 384 : 576;
  // iter915 — resolutionScale lets the caller shrink the raster (0.75, 0.55, 0.4)
  // to fit the m30II buffer without dropping to text mode (which loses Arabic).
  // Round to nearest multiple of 8 (ePOS raster row width requirement).
  const _rs = Math.min(1, Math.max(0.35, Number(resolutionScale) || 1));
  const W = Math.max(64, Math.round((baseW * _rs) / 8) * 8);
  const cols = _colsForPaper(paperMm);
  const { lines } = buildReceiptLines(c, paperMm);

  // iter915 — Guaranteed top-of-receipt header: Restaurant Name (branch),
  // Company Name, and VAT are ALWAYS drawn into the raster image so
  // Arabic-capable customers see them regardless of options toggles.
  // These sit BELOW the logo and ABOVE the standard receipt body.
  // Identity header (restaurant / branch / legal name / VAT / address) now comes from
  // buildReceiptLines, de-duplicated, so every print path shows the same header.
  const _topHeaderLines = [];


  // Monospace font size so `cols` chars fit the paper width (~0.6 em
  // advance per char in monospace). Floor so we never overflow.
  const fs = Math.max(11, Math.floor(W / cols / 0.6));
  const lineH = Math.round(fs * 1.35);

  // Optional logo at the top.
  let logoImg = null, logoW = 0, logoH = 0;
  if (c.logo_url) {
    try {
      // iter915 — If the logo isn't already a Data URI, convert it via
      // thermalLogoOptimizer BEFORE handing to `Image.src`. Prevents the
      // SecurityError raised by `getImageData()` when the fetched image
      // taints the canvas (cross-origin without CORS headers).
      let logoSrc = c.logo_url;
      if (!String(logoSrc).startsWith('data:')) {
        try {
          const { logoUrlToDataUri } = await import('./thermalLogoOptimizer');
          const converted = await logoUrlToDataUri(logoSrc, paperMm);
          if (converted) logoSrc = converted;
        } catch { /* fall through and try direct load below */ }
      }
      logoImg = await new Promise((resolve, reject) => {
        const im = new Image();
        const t = setTimeout(() => reject(new Error('timeout')), 4000);
        im.onload = () => { clearTimeout(t); resolve(im); };
        im.onerror = () => { clearTimeout(t); reject(new Error('load-fail')); };
        im.src = logoSrc; // data URI / same-origin — no CORS needed for draw
      });
      const ratio = logoImg.naturalHeight / Math.max(1, logoImg.naturalWidth);
      // iter767 — logo print size from branding.thermal_logo_size
      // (Small 0.75 / Medium 1.0 / Large 1.2).
      const _scale = Math.min(1.2, Math.max(0.6, Number(c.logo_scale) || 1));
      logoW = Math.min(W, Math.round(W * 0.62 * _scale)); // scale logo with paper width
      logoH = Math.round(logoW * ratio);
      const _cap = Math.round(180 * _scale);
      if (logoH > _cap) { logoH = _cap; logoW = Math.round(logoH / ratio); }
    } catch { logoImg = null; }
  }

  let textH = 0;
  for (const ln of _topHeaderLines) textH += (ln.size === 2 ? 2 : 1) * lineH;
  for (const ln of lines) textH += (ln.size === 2 ? 2 : 1) * lineH;
  const padTop = 6, padBottom = 10, logoGap = logoImg ? 8 : 0;
  const H = padTop + (logoImg ? logoH + logoGap : 0) + textH + padBottom;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';

  let y = padTop;
  if (logoImg) {
    try { ctx.drawImage(logoImg, Math.round((W - logoW) / 2), y, logoW, logoH); } catch { /* skip */ }
    y += logoH + logoGap;
  }
  // iter915 — Draw the guaranteed Company / Restaurant / Branch header
  // BEFORE the standard receipt body so it always shows up on paper.
  for (const ln of _topHeaderLines) {
    const big = ln.size === 2;
    const f = big ? 2 * fs : fs;
    ctx.font = `${ln.bold ? 'bold ' : ''}${f}px monospace`;
    ctx.textAlign = 'left';
    // iter933 — maxWidth=W condenses instead of overflowing. Without it,
    // the 11px fs floor makes `cols` chars wider than the canvas at the
    // 0.4 retry scale and the right-hand column (prices/totals) is clipped.
    ctx.fillText(ln.text || '', 0, y, W);
    y += (big ? 2 : 1) * lineH;
  }
  const M = 6; // side margin (dots) — keeps first/last glyphs out of the printer's dead zone
  // Bidi fix: an LRM after each Arabic run stops following numbers/colons/AM from being
  // pulled into the right-to-left run ("Printed At / تاريخ الطباعة: 2026/10/31 …").
  const _ltr = (t) => String(t || '').replace(/([\u0600-\u06FF][\u0600-\u06FF\s()]*)/g, (m) => `${m.replace(/\s+$/, '')}\u200E${m.match(/\s+$/)?.[0] || ''}`);
  for (const ln of lines) {
    const big = ln.size === 2;
    const f = big ? 2 * fs : fs;
    // monospace base keeps the space-padded column alignment; the browser
    // falls back to an Arabic-capable font per-glyph for Arabic codepoints.
    ctx.font = `${ln.bold ? 'bold ' : ''}${f}px monospace`;
    if (ln.right !== undefined) {
      // Two-column row: label flush left, amount flush right (Arabic glyph widths
      // differ from monospace, so space-padding alone misaligns the right column).
      const rw = Math.min(W / 2, ctx.measureText(ln.right).width);
      ctx.textAlign = 'right';
      ctx.fillText(_ltr(ln.right), W - M, y, W / 2);
      ctx.textAlign = 'left';
      ctx.fillText(_ltr(ln.left), M, y, Math.max(20, W - 2 * M - rw - 8));
    } else if (/^-{8,}$/.test(String(ln.text || '').trim())) {
      ctx.fillRect(M, y + Math.round(lineH / 2) - 1, W - 2 * M, 2); // full-width divider
    } else if (ln.align === 'center') {
      ctx.textAlign = 'center';
      ctx.fillText(_ltr(String(ln.text || '').trim()), W / 2, y, W - 2 * M);
    } else {
      ctx.textAlign = 'left';
      // iter933 — maxWidth condenses instead of overflowing at small retry scales.
      ctx.fillText(_ltr(ln.text), M, y, W - 2 * M);
    }
    y += (big ? 2 : 1) * lineH;
  }

  let px;
  try {
    px = ctx.getImageData(0, 0, W, H).data;
  } catch (e) {
    return { xml: '', status: `tainted-canvas (${(e && e.message) || 'cross-origin logo'})` };
  }
  const rowBytes = Math.ceil(W / 8);
  const packed = new Uint8Array(rowBytes * H);
  for (let yy = 0; yy < H; yy++) {
    for (let xx = 0; xx < W; xx++) {
      const i = (yy * W + xx) * 4;
      const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      const alpha = px[i + 3];
      if (alpha > 64 && gray < 140) packed[yy * rowBytes + (xx >> 3)] |= 0x80 >> (xx & 7);
    }
  }
  let bin = '';
  const CHUNK = 0x4000;
  for (let i = 0; i < packed.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, packed.subarray(i, Math.min(i + CHUNK, packed.length)));
  }
  const b64 = btoa(bin);
  return {
    xml: `<text align="center"/><image width="${W}" height="${H}" color="color_1" mode="mono">${b64}</image><text align="left"/>`,
    status: `raster-receipt (${W}x${H}, ${Math.round(b64.length / 1024)}KB)`,
    // iter933 — also hand back the raw 1-bit bitmap so the native/TCP
    // path can re-encode the SAME pixels as an ESC/POS GS v 0 raster
    // instead of falling back to Arabic-incapable text mode.
    packed, W, H,
  };
}

/* iter933 — Wrap a packed 1-bit bitmap in the ESC/POS "GS v 0" raster
 * command and hex-encode it, which is what DantSu's <img> tag expects.
 * Identical framing to _logoHexFromUrl() in nativeThermalPrint.js. */
function _packedToGsV0Hex(packed, W, H) {
  const bytesPerLine = Math.ceil(W / 8);
  const hx = (n) => (n & 0xff).toString(16).padStart(2, '0');
  let hex = '1d7630' + '00' + hx(bytesPerLine) + hx(bytesPerLine >> 8) + hx(H) + hx(H >> 8);
  for (let i = 0; i < packed.length; i++) hex += hx(packed[i]);
  return hex.toUpperCase();
}

/** iter915 — Scan the receipt content for Arabic codepoints. Returns
 * true when any text field (company, branch, header, footer, item
 * names / modifiers / notes, ZATCA seller name, payment label) contains
 * a character in the Arabic / Arabic-Presentation-Forms blocks. Used by
 * `printViaEposDirect` to force the raster path when text-mode fallback
 * would produce garbled output. */
function _receiptContainsArabic(content) {
  if (!content) return false;
  // Saudi tax-invoice headings / bilingual labels are Arabic → always raster.
  if (isSaudiTaxReceipt(content) || ['both', 'localized'].includes(content.print_language)) return true;
  // U+0600–U+06FF Arabic · U+0750–U+077F Arabic Supplement
  // U+08A0–U+08FF Arabic Extended-A · U+FB50–U+FDFF Arabic Presentation-A
  // U+FE70–U+FEFF Arabic Presentation-B
  const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  const strFields = [
    content.company, content.branch, content.title, content.header,
    content.tenant_header_text, content.footer, content.zatca_seller_name,
    content.payment_label, content.order_type, content.server, content.cashier,
  ];
  for (const s of strFields) if (s && ARABIC_RE.test(String(s))) return true;
  const opts = content.options || {};
  if (opts.arabic_header_text && ARABIC_RE.test(String(opts.arabic_header_text))) return true;
  for (const it of (content.items || [])) {
    if (it?.name && ARABIC_RE.test(String(it.name))) return true;
    if (it?.note && ARABIC_RE.test(String(it.note))) return true;
    for (const m of (it?.modifiers || [])) {
      const mn = typeof m === 'string' ? m : (m?.name || '');
      if (mn && ARABIC_RE.test(String(mn))) return true;
    }
  }
  // Language toggle explicitly targets Arabic → force raster.
  if (content.localized_language === 'ar' && (content.print_language === 'localized' || content.print_language === 'both')) return true;
  if (content.main_language === 'ar') return true;
  return false;
}

/** iter745 — Wrap an ePOS-Print body fragment in the SOAP envelope. */
function _eposEnvelope(body) {  return [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">',
    '<s:Body>',
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">',
    body,
    '</epos-print>',
    '</s:Body>',
    '</s:Envelope>',
  ].join('\n');
}

function _buildEposXml(content, paperMm, logoXml = '') {
  const c = content || {};
  const xmlEscape = (s) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  // iter683 — Delegate to shared line builder so the preview matches.
  const { lines } = buildReceiptLines(c, paperMm);
  const opt = c.options || {};
  // iter727 — `show_zatca_qr` is now opt-OUT (default TRUE) — see the
  // matching `on()` helper in buildReceiptLines for the rationale. The
  // user explicitly asked for QR to print in iter727.
  const showQr = (('show_zatca_qr' in opt) ? !!opt.show_zatca_qr : true) || isSaudiTaxReceipt(c);
  // iter684 — ZATCA QR (Saudi e-invoicing). Render after the company-
  // bottom block via Epson <symbol> element. mode=qrcode_model_2 +
  // level_l + width=6 fits comfortably under the totals on 80mm paper.
  const zatcaPayload = showQr ? buildZatcaQrPayload(c) : '';
  const qrXml = zatcaPayload
    // iter686 — Removed invalid `height="6"` attribute from <symbol>.
    // QR codes are square so height is computed from width — TM-m30II
    // firmware 02.13 was REJECTING the whole XML envelope when it saw
    // an unknown attribute, which caused the entire receipt to fail
    // silently (the symptom the user reported: "since the last update
    // the printer is not printing"). Single-attribute symbol now per
    // the Epson ePOS-Print spec for QR Model 2.
    //
    // iter729 — Wrap the <symbol> in <text align="center"/> ... so the
    // QR prints CENTERED at the bottom (was left-aligned because the
    // preceding text emitted `<text align="left"/>` and the QR
    // inherits that state). We restore left alignment after so any
    // post-QR text (footer feed lines) keeps the established layout.
    //
    // iter738 — ROOT CAUSE of "log says OK but no paper" finally
    // found. Per the official Epson ePOS-Print SDK reference, the
    // QR module-size attribute is `size`, NOT `width`. We had been
    // sending `width="6"` — which the firmware does NOT recognize.
    // Per the iter686 note above, this exact firmware silently drops
    // the WHOLE envelope on an unknown attribute. Symptom matched
    // perfectly: every receipt with a QR (i.e. every POS receipt)
    // ACK'd as 200 OK but printed nothing, while test slips (which
    // never emit a QR because the sample has no VAT) printed fine.
    // Renamed to `size="6"` so the firmware accepts the QR module.
    ? `<text align="center"/><feed line="1"/><symbol type="qrcode_model_2" level="level_l" size="6">${zatcaPayload}</symbol><feed line="1"/><text align="left"/>`
    : '';

  // Compose XML lines.
  // iter691 — FIX: Epson ePOS-Print `<text>` formatting state is STICKY
  // across lines. If we emit `<text width="2" height="2">Total ...</text>`
  // and then `<text>Thank you!</text>` (no attributes), the firmware
  // KEEPS using width=2/height=2 on the Thank-you line — which is why
  // users were seeing the footer print in giant text. Same applies to
  // `em` (bold). Fix: explicitly emit width/height/em on EVERY line so
  // each one resets to its intended size rather than inheriting whatever
  // was last set. Cost is negligible (~16 bytes per line).
  const xmlBody = lines.map((ln) => {
    const w = ln.size === 2 ? '2' : '1';
    const em = ln.bold ? 'true' : 'false';
    return `<text width="${w}" height="${w}" em="${em}">${xmlEscape(ln.text)}\n</text>`;
  }).join('\n');

  return [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">',
    '<s:Body>',
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">',
    // iter681 — Centered logo (if provided) at the very top.
    logoXml ? `<text align="center"/>${logoXml}<text align="left"/>` : '',
    xmlBody,
    // iter684 — ZATCA QR at the bottom (if ZATCA fields are populated).
    qrXml,
    '<feed line="3"/>',
    '<cut type="feed"/>',
    '</epos-print>',
    '</s:Body>',
    '</s:Envelope>',
  ].filter(Boolean).join('\n');
}

/**
 * Fire a print job at the printer's ePOS-Print HTTP endpoint.
 *
 * Tries HTTPS first (required when called from smartoerp.com which is
 * HTTPS — Safari blocks mixed content otherwise). The browser's mixed-
 * content blocker silently drops HTTPS→HTTP fetch requests with no
 * exception thrown, so we MUST use HTTPS here.
 *
 * iter723 — Content-Type now `text/plain;charset=UTF-8` instead of
 * `text/xml; charset=utf-8`. Reason: with `mode: 'no-cors'` the Fetch
 * spec REQUIRES the browser to drop every non-CORS-safelisted header.
 * `text/xml` is NOT safelisted, so what actually went on the wire to
 * the printer was whatever the browser defaulted to (typically
 * `text/plain;charset=UTF-8`) — but our code believed it was sending
 * `text/xml` which is what the Epson TM-m30II ePOS-Print CGI script
 * advertises as required. In practice the printer's CGI reads stdin
 * as raw XML regardless of the Content-Type declared (it's a Bourne
 * shell script that pipes stdin into the parser). Setting Content-Type
 * to `text/plain;charset=UTF-8` (which IS safelisted) means the header
 * survives no-cors mode and we explicitly send something the printer
 * accepts. SOAPAction was also dropped silently — verified by the
 * production print log capturing every attempt as "OK" while no paper
 * came out. Reference: Fetch spec §4.5 step 5 + CORS-safelisted
 * request-header definition.
 *
 * iter723 — Also added a 1.5-second per-printer/per-order debounce.
 * The production log captured TWO prints for order #17 fired ~500 ms
 * apart (auto-print on checkout + a manual PRINT-button tap). On the
 * TM-m30II, two concurrent HTTPS POSTs to /cgi-bin/epos/service.cgi
 * can land in the printer's request queue out-of-order and the second
 * one cancels the first's print buffer — net result: BOTH receipts
 * fail to print even though both fetches resolve `OK`. The debounce
 * coalesces back-to-back duplicates so only the first actually fires.
 */
import { logPrintEvent, isCaptureXmlEnabled, isRasterModeEnabled, getPrintScale } from './printLogger';
import { tailorReceiptLines } from './tailorReceipt';

// iter723 — Recent prints memo: { 'printer_id|order_number': last_ts_ms }
const _RECENT_PRINTS = new Map();
const _DEBOUNCE_MS = 1500;

// iter748 — Parse the Epson ePOS-Print SOAP response so we can FINALLY
// see WHY a job prints "OK" on the wire but produces no paper. Epson
// returns e.g. `<response success="false" code="EPTR_REC" status="..."/>`.
// For 5+ iterations we used mode:'no-cors' which made this response
// opaque — we were debugging blind. Epson's service.cgi sends
// `Access-Control-Allow-Origin: *`, so a CORS "simple request"
// (text/plain, no custom headers → no preflight) CAN read it.
function _parseEposResponse(text) {
  const out = { success: null, code: '', status: '', raw: (text || '').slice(0, 300) };
  if (!text) return out;
  const s = text.match(/success\s*=\s*"?(true|false)"?/i);
  if (s) out.success = s[1].toLowerCase() === 'true';
  const c = text.match(/code\s*=\s*"([^"]*)"/i);
  if (c) out.code = c[1];
  const st = text.match(/status\s*=\s*"([^"]*)"/i);
  if (st) out.status = st[1];
  return out;
}

function _eposCodeHint(code) {
  const map = {
    EPTR_COVER_OPEN: 'Printer cover is OPEN — close it.',
    EPTR_REC_EMPTY: 'OUT OF PAPER — load a new roll.',
    EPTR_REC: 'Receipt paper / mechanical error — check paper & cover.',
    EPTR_AUTOMATICAL: 'Automatic recoverable error (cover/paper/jam). Open & reseat the roll.',
    EPTR_UNRECOVERABLE: 'Unrecoverable printer error — power-cycle the printer.',
    EPTR_BATTERY_LOW: 'Printer battery low.',
    SchemaError: 'The printer rejected the XML as invalid (SchemaError).',
    DeviceNotFound: 'Wrong device id (devid). Set devid to "local_printer" in Settings → Printers → Edit.',
    EX_BADPORT: 'Printer port/devid mismatch. Check devid = "local_printer".',
    EX_TIMEOUT: 'Printer timed out internally.',
    JobNotFound: 'Job not found.',
    ASB_NO_RESPONSE: 'Printer offline / not responding (ASB).',
    Poweroff: 'Printer is powered off / asleep.',
  };
  return map[code] || (code ? `Printer returned error code "${code}".` : '');
}

// iter748 — POST ePOS XML and READ the response. Tries cors first
// (readable). If the browser blocks the read (printer not sending CORS
// headers, or self-signed cert not yet accepted) it falls back to a
// no-cors fire-and-forget so printing still works, but flags that the
// result is unreadable. Returns { sent, readable, parsed, httpStatus, err }.
async function _postAndReadEpos(url, xml, timeoutMs = 8000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const resp = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, // CORS-safelisted → no preflight
      body: xml,
      signal: ctrl.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);
    let body = '';
    try { body = await resp.text(); } catch { /* opaque */ }
    return { sent: true, readable: true, parsed: _parseEposResponse(body), httpStatus: resp.status, err: '' };
  } catch (e) {
    clearTimeout(timer);
    const msg = String((e && e.message) || e || 'fetch failed');
    if (msg.includes('abort')) {
      return { sent: false, readable: false, parsed: null, httpStatus: 0, err: 'timeout' };
    }
    // CORS/cert blocked the readable request — fall back to fire-and-forget.
    try {
      const ctrl2 = new AbortController();
      const t2 = setTimeout(() => ctrl2.abort(), timeoutMs);
      await fetch(url, {
        method: 'POST', mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: xml, signal: ctrl2.signal, cache: 'no-store',
      });
      clearTimeout(t2);
      return { sent: true, readable: false, parsed: null, httpStatus: 0, err: msg };
    } catch (e2) {
      const msg2 = String((e2 && e2.message) || e2);
      return { sent: false, readable: false, parsed: null, httpStatus: 0, err: msg2.includes('abort') ? 'timeout' : msg2 };
    }
  }
}

/**
 * iter — Build DantSu formatted-text for native TCP printing (Android app).
 * Reuses buildReceiptLines so the layout matches the ePOS/preview output.
 * Each line's text is already padded to the paper width, so we left-align
 * ([L]) and only wrap bold / double-size markup. The ZATCA QR is appended
 * via DantSu's <qrcode> tag so the printer renders it natively.
 */
function _buildDantsuText(content, paperMm, logoHex = '') {
  const { lines } = buildReceiptLines(content, paperMm);
  // Escape characters that DantSu would otherwise treat as markup, BEFORE
  // we add our own <b>/<font> tags.
  const esc = (s) => String(s ?? '').replace(/</g, '(').replace(/>/g, ')');
  const rows = [];
  // iter932 — LOGO PARITY. iter930 added the logo to the Bluetooth/USB
  // DantSu builder (buildFormattedReceipt in nativeThermalPrint.js) but
  // NOT to this one, which is the builder used for NETWORK printers over
  // raw TCP inside the Android APK. Result: browser receipts (ePOS raster)
  // printed the logo while APK receipts silently printed without it.
  // DantSu renders <img> from the same GS v 0 raster hex used by iter930.
  if (logoHex) rows.push(`[C]<img>${logoHex}</img>`);
  for (const ln of (lines || [])) {
    let t = esc(ln.text);
    if (ln.bold) t = `<b>${t}</b>`;
    if (ln.size === 2) t = `<font size='big'>${t}</font>`;
    rows.push(`[L]${t}`);
  }
  const opt = content?.options || {};
  const showQr = (('show_zatca_qr' in opt) ? !!opt.show_zatca_qr : true) || isSaudiTaxReceipt(content);
  const qp = showQr ? buildZatcaQrPayload(content) : '';
  if (qp) {
    rows.push('[C] ');
    rows.push(`[C]<qrcode size='25'>${qp}</qrcode>`);
  }
  rows.push('[C] ');
  return rows.join('\n');
}

export async function printViaEposDirect(printer, content, { openDrawer = false } = {}) {
  const _started = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  const _log = (result) => {
    try {
      const _ended = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      logPrintEvent({
        channel: 'epos-direct',
        ok: !!result.ok,
        printer: {
          name: printer?.name,
          ip: printer?.ip_address,
          deviceId: result.deviceId || (printer?.epos_device_id || 'local_printer'),
          paper_width: printer?.paper_width,
        },
        receipt: content,
        result,
        duration_ms: Math.round(_ended - _started),
      });
    } catch { /* never block printing on logger failure */ }
    return result;
  };
  const ip = (printer?.ip_address || '').trim();
  if (!ip) return _log({ ok: false, error: 'no-ip', msg: 'Printer has no IP address.' });

  // iter723 — Debounce: skip near-duplicate fires for the same
  // printer + order_number within 1.5 s. Logged as `debounced` so the
  // operator can see in the Print Log that a duplicate was suppressed.
  const orderKey = String(content?.order_number || content?.check_number || content?.reference || '');
  if (orderKey) {
    const dbKey = `${printer?.id || ip}|${orderKey}`;
    const now = Date.now();
    const last = _RECENT_PRINTS.get(dbKey) || 0;
    if (now - last < _DEBOUNCE_MS) {
      return _log({ ok: true, mode: 'epos-direct-debounced', deviceId: (printer?.epos_device_id || 'local_printer'), ip,
                    msg: `Duplicate print of order ${orderKey} suppressed (${now - last}ms after previous)` });
    }
    _RECENT_PRINTS.set(dbKey, now);
    // Trim the memo so it can't grow unboundedly during a long shift.
    if (_RECENT_PRINTS.size > 200) {
      const cutoff = now - 5 * 60 * 1000; // 5 minutes
      for (const [k, t] of _RECENT_PRINTS) {
        if (t < cutoff) _RECENT_PRINTS.delete(k);
      }
    }
  }

  const paperMm = parseInt(String(printer?.paper_width || '80').replace('mm', ''), 10) || 80;

  // iter — Native Android app: print over raw TCP (port 9100) via the
  // SmartoPrinter plugin. This bypasses the browser's self-signed-HTTPS
  // cert / mixed-content / CORS restrictions that block ePOS-over-HTTPS
  // inside the Android WebView — the root cause of "nothing prints" on
  // Android network printers. iOS / desktop / Chrome fall through to the
  // ePOS HTTPS path below (isNativeThermalAvailable() is false there).
  try {
    const { isNativeThermalAvailable, printNativeTcp, _logoHexFromUrl } = await import('./nativeThermalPrint');
    if (isNativeThermalAvailable()) {
      const chars = paperMm <= 58 ? 32 : 48;
      const widthMM = paperMm <= 58 ? 48 : 72;
      const port = parseInt(String(printer?.tcp_port || printer?.port || 9100), 10) || 9100;
      // iter932 — Resolve the branding logo to a GS v 0 raster hex so the
      // network/TCP path prints it too. Best-effort: any failure leaves
      // logoHex empty and the receipt prints exactly as it did before.
      let _logoHex = '';
      try {
        if (content?.logo_url) {
          // iter934 — convert the server logo URL to a data URI FIRST so the
          // canvas read in _logoHexFromUrl never runs on a cross-origin URL
          // (tainted canvas → getImageData throws inside the APK).
          let _logoSrc = content.logo_url;
          try {
            const { logoUrlToDataUri } = await import('./thermalLogoOptimizer');
            const _conv = await logoUrlToDataUri(_logoSrc, paperMm);
            if (_conv) _logoSrc = _conv;
          } catch (_) { /* fall back to raw url */ }
          _logoHex = (await _logoHexFromUrl(_logoSrc, paperMm)) || '';
        }
      } catch (e) {
        _logoHex = '';
        console.error('[thermal] logo conversion failed (TCP path); printing without logo:', content?.logo_url, e);
      }
      // iter933 — ARABIC PARITY. DantSu text mode uses the printer's
      // built-in fonts, which have NO Arabic glyphs, so an Arabic receipt
      // sent as text prints blank/garbled. The ePOS path already solves
      // this by rasterising the WHOLE receipt — do the same here and hand
      // DantSu one <img>. The logo is drawn INSIDE that raster, so no
      // separate logoHex is needed on this branch.
      let text = null;
      if (_receiptContainsArabic(content)) {
        for (const _sc of [1, 0.75, 0.55, 0.4]) {
          let rr = null;
          try { rr = await _renderReceiptRasterImage(content, paperMm, _sc); } catch { rr = null; }
          if (!rr || !rr.packed) continue;
          const _hex = _packedToGsV0Hex(rr.packed, rr.W, rr.H);
          // Keep the bridge payload sane; shrink first, only accept an
          // oversized one at the smallest scale.
          if (_hex.length > 400000 && _sc !== 0.4) continue;
          const _rows = [`[C]<img>${_hex}</img>`];
          const _opt = content?.options || {};
          const _showQr = (('show_zatca_qr' in _opt) ? !!_opt.show_zatca_qr : true) || isSaudiTaxReceipt(content);
          const _qp = _showQr ? buildZatcaQrPayload(content) : '';
          if (_qp) { _rows.push('[C] '); _rows.push(`[C]<qrcode size='25'>${_qp}</qrcode>`); }
          _rows.push('[C] ');
          text = _rows.join('\n');
          break;
        }
      }
      // Latin-only receipts — and any raster failure — keep the text path.
      if (text === null) text = _buildDantsuText(content, paperMm, _logoHex);
      const r = await printNativeTcp({ address: ip, port, text, widthMM, chars, openDrawer });
      if (r.ok) {
        return _log({ ok: true, mode: 'native-tcp', deviceId: 'tcp', ip,
                      msg: `Printed via native TCP ${ip}:${port} (Android app)` });
      }
      // Native TCP failed — log it, then fall through to ePOS HTTPS as a backstop.
      _log({ ok: false, mode: 'native-tcp', error: 'native-tcp-failed', ip,
             msg: `Native TCP to ${ip}:${port} failed (${r.error}). Falling back to ePOS HTTPS.` });
    }
  } catch { /* plugin unavailable — fall through to ePOS HTTPS */ }

  const optForQr = content?.options || {};
  const qrEmitted = (('show_zatca_qr' in optForQr) ? !!optForQr.show_zatca_qr : true) || isSaudiTaxReceipt(content);
  let logoStatus, qrStatus, xml;

  // iter915 — Arabic detection. When any user-visible field contains
  // Arabic codepoints (U+0600..U+06FF etc.), the receipt MUST stay on
  // the raster path — the ePOS text mode has no Arabic glyphs and would
  // print blanks / question marks. We shrink the image resolution
  // instead so the payload fits the m30II buffer.
  const _hasArabic = _receiptContainsArabic(content);

  if (isRasterModeEnabled()) {
    // iter745 — Whole-receipt raster path. Renders logo + all text into
    // ONE monochrome <image> so Arabic prints correctly. The ZATCA QR is
    // still emitted natively (printer renders it crisply) AFTER the image.
    const r = await _renderReceiptRasterImage(content, paperMm);
    logoStatus = r.status;
    if (r.xml) {
      const zatcaPayload = qrEmitted ? buildZatcaQrPayload(content) : '';
      const qrXml = zatcaPayload
        ? `<text align="center"/><feed line="1"/><symbol type="qrcode_model_2" level="level_l" size="6">${zatcaPayload}</symbol><feed line="1"/><text align="left"/>`
        : '';
      qrStatus = zatcaPayload ? 'emitted' : (qrEmitted ? 'no-zatca-fields' : 'disabled-in-settings');
      xml = _eposEnvelope([r.xml, qrXml, '<feed line="3"/>', '<cut type="feed"/>'].filter(Boolean).join('\n'));
      // iter884 — 200KB size guard. Some low-buffer TM-m30II / TM-T88VI
      // firmwares silently DROP the print job (HTTP 200, no error, no
      // paper) when the raster image pushes the XML above ~200KB.
      // iter915 — When the receipt contains Arabic, we CANNOT fall back
      // to text mode (ePOS text fonts have no Arabic glyphs) — instead
      // re-render the raster at progressively lower resolution until
      // the payload fits under 200KB. Non-Arabic receipts keep the
      // original text-mode downgrade for backwards compat.
      const xmlBytes = new Blob([xml]).size;
      if (xmlBytes > 200 * 1024) {
        if (_hasArabic) {
          const RETRY_SCALES = [0.75, 0.55, 0.4];
          let shrunk = null;
          for (const scale of RETRY_SCALES) {
            const rr = await _renderReceiptRasterImage(content, paperMm, scale);
            if (!rr.xml) continue;
            const zatcaPayload2 = qrEmitted ? buildZatcaQrPayload(content) : '';
            const qrXml2 = zatcaPayload2
              ? `<text align="center"/><feed line="1"/><symbol type="qrcode_model_2" level="level_l" size="6">${zatcaPayload2}</symbol><feed line="1"/><text align="left"/>`
              : '';
            const candidateXml = _eposEnvelope([rr.xml, qrXml2, '<feed line="3"/>', '<cut type="feed"/>'].filter(Boolean).join('\n'));
            const candidateBytes = new Blob([candidateXml]).size;
            if (candidateBytes <= 200 * 1024) {
              xml = candidateXml;
              logoStatus = `raster-shrunk-${Math.round(scale * 100)}%-${Math.round(candidateBytes / 1024)}KB (arabic-safe)`;
              shrunk = true;
              break;
            }
            shrunk = { rr, candidateXml, candidateBytes, scale };
          }
          if (shrunk !== true) {
            // Even at min scale it's still oversized — send it anyway.
            // Better a possibly-dropped Arabic receipt than a guaranteed
            // garbled one in text mode.
            if (shrunk && shrunk.candidateXml) {
              xml = shrunk.candidateXml;
              logoStatus = `raster-shrunk-min-${Math.round(shrunk.scale * 100)}%-${Math.round(shrunk.candidateBytes / 1024)}KB (arabic-safe, still oversize)`;
            } else {
              logoStatus = `raster-shrink-failed-${Math.round(xmlBytes / 1024)}KB (arabic-safe fallback)`;
            }
          }
        } else {
          const logoResult = await _renderLogoEposImage(content?.logo_url, paperMm, content?.logo_scale);
          logoStatus = `raster-oversize-${Math.round(xmlBytes / 1024)}KB→text`;
          xml = _buildEposXml(content, paperMm, logoResult.xml || '');
        }
      }
    } else {
      // Raster render failed (e.g. tainted canvas) — fall back to text.
      const logoResult = await _renderLogoEposImage(content?.logo_url, paperMm, content?.logo_scale);
      logoStatus = `raster-failed→text (${r.status})`;
      qrStatus = qrEmitted ? 'emitted' : 'disabled-in-settings';
      xml = _buildEposXml(content, paperMm, logoResult.xml || '');
    }
  } else {
    // iter681/727 — Legacy text path: per-line <text> + a separate logo
    // <image> + native QR. Kept as the user-selectable fallback.
    const logoResult = await _renderLogoEposImage(content?.logo_url, paperMm, content?.logo_scale);
    logoStatus = logoResult.status || 'unknown';
    qrStatus = qrEmitted ? 'emitted' : 'disabled-in-settings';
    xml = _buildEposXml(content, paperMm, logoResult.xml || '');
  }
  // iter741 — Developer-toggle: capture the raw XML into the Print
  // Log so debug-mode operators can see EXACTLY what went on the wire.
  // Off by default to keep localStorage small.
  const captureXml = isCaptureXmlEnabled();
  if (openDrawer && xml) xml = xml.replace('</epos-print>', '<pulse drawer="drawer_1" time="pulse_100"/>\n</epos-print>');

  // iter678 — Device ID is now configurable per-printer. The Epson
  // TM-m30II's WebConfig → ePOS-Print page exposes its own "Device ID"
  // field which ships with arbitrary defaults (some "local_printer",
  // some "<IP>", some custom). When the value the printer expects and
  // the value we send don't match, the printer ACKS our HTTPS request
  // but silently refuses to print. We now let the operator type the
  // exact value the printer is using in Settings → Printers → Edit.
  const deviceId = (printer?.epos_device_id || '').trim() || 'local_printer';
  // ePOS-Print supports both HTTP (80) and HTTPS (443). When called from
  // smartoerp.com (HTTPS) we MUST use the HTTPS endpoint, otherwise the
  // browser blocks the request as mixed content.
  const url = `https://${ip}/cgi-bin/epos/service.cgi?devid=${encodeURIComponent(deviceId)}&timeout=10000`;

  try {
    const r = await _postAndReadEpos(url, xml, 8000);
    if (r.err === 'timeout') {
      return _log({
        ok: false, error: 'fetch-failed', deviceId, ip, logo: logoStatus, qr: qrStatus,
        xml: captureXml ? xml : '',
        msg: `Printer at ${ip} did not respond within 8s (devid="${deviceId}"). Check it\u2019s on the same Wi-Fi.`,
      });
    }
    if (r.readable && r.parsed) {
      // iter884 — Only treat r.parsed.success === true as success.
      // Previously we treated `null` (unparsed) as OK, which masked
      // real rejections. Also log the raw response body on failure so
      // debug operators can see the actual printer error message.
      const ok = r.parsed.success === true;
      const detail = `HTTP ${r.httpStatus} · success=${r.parsed.success} · code=${r.parsed.code || '-'} · status=${r.parsed.status || '-'}`;
      const rawSnippet = (r.parsed.raw || '').toString().slice(0, 400);
      return _log({
        ok,
        mode: 'epos-direct',
        deviceId, ip, logo: logoStatus, qr: qrStatus,
        eposResult: detail,
        msg: ok
          ? `Printer ACCEPTED the job ✓ (${detail})`
          : (r.parsed.success === false
              ? `Printer REJECTED the job. ${_eposCodeHint(r.parsed.code)} (${detail}) — raw: ${rawSnippet}`
              : `Response unparsed (treated as failure): ${rawSnippet}`),
        // iter884 — Always capture XML + raw response body on failure so
        // the print log has enough to diagnose. On success only capture
        // XML when the operator toggle is on.
        xml: ok ? (captureXml ? xml : '') : xml,
        rawResponse: ok ? '' : rawSnippet,
      });
    }
    // iter934 — CONNECTIVITY: only claim success if the request actually
    // LEFT the device. _postAndReadEpos returns sent:false when BOTH the
    // cors and the no-cors attempt threw, which means the printer was
    // never reached at all (untrusted self-signed cert, stale/wrong IP
    // after a DHCP change, different subnet, or printer powered off).
    // The old code fell straight through to ok:true, so a total transport
    // failure was reported to the cashier as a successful print — the
    // reason every real connectivity failure was invisible and "printing
    // worked but no paper came out".
    if (!r.sent) {
      return _log({
        ok: false, error: 'unreachable', mode: 'epos-direct', deviceId, ip,
        logo: logoStatus, qr: qrStatus, xml,
        eposResult: 'not sent (printer unreachable)',
        msg: `Could not reach the printer at ${ip} (${r.err}). NOTHING was printed. Check that the printer is powered on and on the same Wi-Fi as this device, that its IP is still ${ip}, and that https://${ip}/ has been opened once in this browser to accept its certificate.`,
      });
    }
    // Sent, but the response could not be read (CORS/cert) — printing may still work.
    return _log({
      ok: true, mode: 'epos-direct', deviceId, ip, logo: logoStatus, qr: qrStatus,
      eposResult: 'sent (response unreadable)',
      msg: `Job sent but the printer\u2019s response could not be read (${r.err}). If nothing prints, accept the cert at https://${ip}/ and ensure the printer allows browser (CORS) requests.`,
      xml: captureXml ? xml : '',
    });
  } catch (e) {
    const msg = String((e && e.message) || e || 'fetch failed');
    return _log({
      ok: false,
      error: 'fetch-failed',
      deviceId,
      ip,
      logo: logoStatus,
      qr: qrStatus,
      xml: captureXml ? xml : '',
      msg: msg.includes('abort')
        ? `Printer at ${ip} did not respond within 8s (devid="${deviceId}"). Check it\u2019s on the same Wi-Fi.`
        : `Browser blocked the fetch to ${ip} (${msg}). Most common cause: the printer\u2019s self-signed HTTPS cert hasn\u2019t been accepted yet — visit https://${ip}/ once and tap "Visit Website".`,
    });
  }
}

/**
 * Convenience: open the printer's HTTPS cert page so the cashier can
 * accept the self-signed certificate. Once accepted, ePOS-Print Direct
 * works for ~30 days on this Safari profile.
 */
export function openPrinterCertPage(ip) {
  if (!ip) return;
  window.open(`https://${ip}/`, '_blank', 'noopener,noreferrer');
}

/**
 * iter732 — Flush the in-memory print debounce cache (`_RECENT_PRINTS`).
 *
 * When the operator suspects a print is being suppressed as a
 * "duplicate" (e.g. they tapped PRINT, nothing came out, they re-tapped
 * within 1.5s and the second tap was silently debounced), this lets
 * them wipe the cache so the next tap fires fresh.
 *
 * Returns { cleared: N } where N is the number of entries removed,
 * so the caller can show a toast confirming what happened.
 */
export function clearPrintDebounceCache() {
  const n = _RECENT_PRINTS.size;
  _RECENT_PRINTS.clear();
  return { cleared: n };
}

/**
 * iter732 — Low-level XML POST helper shared by resetEposPrinter() and
 * printDiagnosticSlip(). Same transport choices as printViaEposDirect()
 * (text/plain Content-Type, no-cors mode, 8s abort, HTTPS) so behaviour
 * is identical and the Print Log captures every attempt the same way.
 */
async function _postEposXml(printer, xmlBody, channel) {
  const _started = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  const ip = (printer?.ip_address || '').trim();
  const deviceId = (printer?.epos_device_id || '').trim() || 'local_printer';
  const _logResult = (result) => {
    try {
      const _ended = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      logPrintEvent({
        channel,
        ok: !!result.ok,
        printer: { name: printer?.name, ip, deviceId, paper_width: printer?.paper_width },
        receipt: { order_number: channel, items_count: 0 },
        result,
        duration_ms: Math.round(_ended - _started),
      });
    } catch { /* never block on logger failure */ }
    return result;
  };
  if (!ip) return _logResult({ ok: false, error: 'no-ip', msg: 'Printer has no IP address.' });

  const url = `https://${ip}/cgi-bin/epos/service.cgi?devid=${encodeURIComponent(deviceId)}&timeout=10000`;
  const xml = [
    '<?xml version="1.0" encoding="utf-8"?>',
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">',
    '<s:Body>',
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">',
    xmlBody,
    '</epos-print>',
    '</s:Body>',
    '</s:Envelope>',
  ].join('\n');
  // iter741 — capture raw XML for diagnostic slips & resets too.
  const captureXml = isCaptureXmlEnabled();

  try {
    const r = await _postAndReadEpos(url, xml, 8000);
    if (r.err === 'timeout') {
      return _logResult({
        ok: false, error: 'fetch-failed', deviceId, ip, xml: captureXml ? xml : '',
        msg: `Printer at ${ip} did not respond within 8s (devid="${deviceId}"). Check it\u2019s on the same Wi-Fi.`,
      });
    }
    if (r.readable && r.parsed) {
      const ok = r.parsed.success !== false;
      const detail = `HTTP ${r.httpStatus} · success=${r.parsed.success} · code=${r.parsed.code || '-'} · status=${r.parsed.status || '-'}`;
      return _logResult({
        ok, mode: channel, deviceId, ip,
        eposResult: detail,
        msg: r.parsed.success === false
          ? `Printer REJECTED the job. ${_eposCodeHint(r.parsed.code)} (${detail})`
          : (r.parsed.success === true ? `Printer ACCEPTED the job ✓ (${detail})` : `Response unparsed: ${r.parsed.raw}`),
        xml: (captureXml || r.parsed.success === false) ? xml : '',
      });
    }
    return _logResult({
      ok: true, mode: channel, deviceId, ip,
      eposResult: 'sent (response unreadable)',
      msg: `Job sent but response unreadable (${r.err}). If nothing prints, accept the cert at https://${ip}/ and allow browser (CORS) requests on the printer.`,
      xml: captureXml ? xml : '',
    });
  } catch (e) {
    const msg = String((e && e.message) || e || 'fetch failed');
    return _logResult({
      ok: false,
      error: 'fetch-failed',
      deviceId,
      ip,
      xml: captureXml ? xml : '',
      msg: msg.includes('abort')
        ? `Printer at ${ip} did not respond within 8s (devid="${deviceId}"). Check it\u2019s on the same Wi-Fi.`
        : `Browser blocked the fetch to ${ip} (${msg}). Most common cause: the printer\u2019s self-signed HTTPS cert hasn\u2019t been accepted yet — visit https://${ip}/ once and tap "Visit Website".`,
    });
  }
}

/**
 * iter732 — Send a hardware reset / buffer flush to the printer.
 *
 * Uses the Epson ePOS-Print `<recovery/>` element which is documented
 * to clear an "ASB error" (printer-stuck state, e.g. cover-open, paper-
 * jam recovery, ESC/POS buffer corruption) without requiring a power
 * cycle. We also clear the in-app debounce cache so the next print
 * isn't suppressed as a duplicate.
 *
 * Returns the same shape as printViaEposDirect so callers can reuse
 * their existing UI plumbing (success toast on ok=true).
 */
export async function resetEposPrinter(printer) {
  clearPrintDebounceCache();
  // <recovery/> alone — no <feed>, no <cut>. Some firmwares reject
  // mixing recovery with print operations in the same envelope.
  return _postEposXml(printer, '<recovery/>', 'epos-reset');
}

/**
 * iter732 — Send the smallest possible diagnostic slip: one line of
 * text + a feed + a cut. No logo, no QR, no totals — isolates "is the
 * hardware responding at all?" from "is the receipt payload confusing
 * the firmware?". If THIS prints but full receipts don't, the bug is
 * in the receipt XML generation (size, QR, image). If this DOESN'T
 * print, the issue is connectivity / device-id / cert / hardware.
 */
export async function printDiagnosticSlip(printer) {
  const ts = new Date().toLocaleString();
  const body = [
    '<text width="2" height="2" em="true">SMARTO\n</text>',
    '<text width="1" height="1" em="false">Diagnostic slip\n</text>',
    `<text width="1" height="1" em="false">${ts}\n</text>`,
    '<feed line="3"/>',
    '<cut type="feed"/>',
  ].join('');
  return _postEposXml(printer, body, 'epos-diagnostic');
}

/**
 * iter745 — Image-only diagnostic. Sends ONE small raster `<image>`
 * element (a canvas-drawn "IMAGE OK" box) — NO text, NO QR, NO logo
 * fetch. Decisively isolates whether the printer's firmware renders
 * raster images at all.
 *
 *   - If the diagnostic slip (text) prints but THIS does NOT → the
 *     `<image>` element is the silent-drop culprit; rendering the whole
 *     receipt as a bitmap will NOT help and we must print text instead.
 *   - If THIS prints → raster works; we can safely render the full
 *     Arabic receipt as one image (the Foodics approach).
 *
 * The raster is generated programmatically with the exact same packing
 * path (`ceil(w/8)` bytes/row, MSB first, 1=black) the real logo uses,
 * so a pass here proves the logo packing is wire-correct too.
 */
export async function printImageTestSlip(printer) {
  if (typeof document === 'undefined') {
    return { ok: false, error: 'no-document', msg: 'Image test needs a browser.' };
  }
  const paperMm = printer?.paper_width === '58mm' ? 58 : 80;
  const w = paperMm <= 58 ? 256 : 384; // multiple of 8
  const h = 96;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#000000';
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#000000';
  ctx.strokeRect(6, 6, w - 12, h - 12);
  ctx.font = 'bold 40px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('IMAGE OK', w / 2, h / 2);

  const px = ctx.getImageData(0, 0, w, h).data;
  const mono = new Uint8Array(w * h);
  for (let i = 0, j = 0; i < px.length; i += 4, j++) {
    const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
    mono[j] = gray < 128 ? 1 : 0;
  }
  const rowBytes = Math.ceil(w / 8);
  const packed = new Uint8Array(rowBytes * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mono[y * w + x]) packed[y * rowBytes + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  let bin = '';
  for (let i = 0; i < packed.length; i++) bin += String.fromCharCode(packed[i]);
  const b64 = btoa(bin);

  const body = [
    '<text width="1" height="1" em="false">Image render test:\n</text>',
    `<text align="center"/><image width="${w}" height="${h}" color="color_1" mode="mono">${b64}</image><text align="left"/>`,
    '<feed line="3"/>',
    '<cut type="feed"/>',
  ].join('');
  return _postEposXml(printer, body, 'epos-image-test');
}
